/**
 * Fx.ts — a fixed, full-screen overlay canvas for GLOBAL celebratory effects
 * (confetti, coin bursts, ring shockwaves) triggered by big game events.
 * Independent of MineCanvas so effects float above the whole UI. Guarded so it
 * safely no-ops without a 2D context (jsdom / unsupported).
 */
interface Confetto {
  x: number; y: number; vx: number; vy: number;
  rot: number; vr: number; size: number; color: string;
  life: number; shape: 'rect' | 'circle' | 'coin';
}

const PALETTE = ['#ffb648', '#ffd25a', '#5fd0ff', '#3ddc84', '#ff6b6b', '#b388ff', '#ffffff'];

export class Fx {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private items: Confetto[] = [];
  private rings: Array<{ x: number; y: number; r: number; max: number; color: string }> = [];
  private w = 0;
  private h = 0;
  private dpr = 1;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'fx-overlay';
    this.ctx = safeContext(this.canvas);
    this.resize();
  }

  resize(): void {
    this.dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
    this.w = (typeof window !== 'undefined' && window.innerWidth) || 800;
    this.h = (typeof window !== 'undefined' && window.innerHeight) || 600;
    this.canvas.width = Math.floor(this.w * this.dpr);
    this.canvas.height = Math.floor(this.h * this.dpr);
    if (this.ctx) this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  /** Burst of confetti from a point (defaults to top-center). */
  confetti(x = this.w / 2, y = this.h * 0.3, count = 80): void {
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 120 + Math.random() * 380;
      const shapes: Confetto['shape'][] = ['rect', 'circle'];
      this.items.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 220,
        rot: Math.random() * Math.PI,
        vr: (Math.random() * 2 - 1) * 10,
        size: 5 + Math.random() * 7,
        color: PALETTE[(Math.random() * PALETTE.length) | 0]!,
        life: 1,
        shape: shapes[(Math.random() * shapes.length) | 0]!,
      });
    }
    this.cap();
  }

  /** A spray of gold coins (e.g. on a big sale / prestige). */
  coins(x = this.w / 2, y = this.h * 0.5, count = 30): void {
    for (let i = 0; i < count; i++) {
      const a = -Math.PI / 2 + (Math.random() * 2 - 1) * 0.9;
      const sp = 200 + Math.random() * 360;
      this.items.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        rot: 0, vr: (Math.random() * 2 - 1) * 8,
        size: 9 + Math.random() * 6,
        color: Math.random() < 0.5 ? '#ffd25a' : '#ffb648',
        life: 1, shape: 'coin',
      });
    }
    this.cap();
  }

  /** An expanding ring shockwave. */
  ring(x: number, y: number, color = '#ffce82', max = 160): void {
    this.rings.push({ x, y, r: 4, max, color });
    if (this.rings.length > 24) this.rings.shift();
  }

  private cap(): void {
    if (this.items.length > 1200) this.items.splice(0, this.items.length - 1200);
  }

  render(dt: number): void {
    // Advance simulation regardless of context so state never accumulates.
    for (const p of this.items) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 620 * dt;
      p.vx *= 0.99;
      p.rot += p.vr * dt;
      p.life -= dt * 0.55;
    }
    this.items = this.items.filter((p) => p.life > 0 && p.y < this.h + 40);
    for (const r of this.rings) r.r += (r.max - r.r) * Math.min(1, dt * 6);
    this.rings = this.rings.filter((r) => r.max - r.r > 2);

    const ctx = this.ctx;
    if (!ctx) return;
    ctx.clearRect(0, 0, this.w, this.h);

    for (const r of this.rings) {
      const alpha = Math.max(0, 1 - r.r / r.max);
      ctx.globalAlpha = alpha * 0.7;
      ctx.strokeStyle = r.color;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
      ctx.stroke();
    }

    for (const p of this.items) {
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === 'coin') {
        ctx.beginPath();
        ctx.ellipse(0, 0, p.size / 2, p.size / 2.6, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(0,0,0,0.18)';
        ctx.fillRect(-p.size / 6, -p.size / 2.6, p.size / 3, p.size / 1.3);
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }
}

function safeContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return typeof canvas.getContext === 'function' ? canvas.getContext('2d') : null;
  } catch {
    return null;
  }
}
